import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Sparkles, TrendingUp, Shield, RefreshCw, Zap, Search, Megaphone, Clock, Cloud, Lock, Rocket, ChevronDown, BookOpen, FileText, HelpCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import dashboardHero from "@/assets/orbitrank-dashboard-hero.png";
import logoFull from "@/assets/orbitrank-logo-v3.png";
import TrustBridge from "./TrustBridge";

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "Resources", href: "#", caret: true },
  { label: "Blog", href: "/blog" },
];

const TRUST_LOGOS = ["DigitalFlow", "Rankly", "SERPIONE", "PeakReach", "GrowthLab", "ClickBoost"];

const FEATURE_CARDS = [
  {
    icon: Zap,
    title: "AI-Powered SEO Engine",
    desc: "Our engine works 24/7 to find opportunities, create content, build links and improve your rankings automatically.",
    color: "#00FF88",
    glow: "rgba(0,255,136,0.18)",
    cta: "Learn more",
  },
  {
    icon: Search,
    title: "AI Search & Content",
    desc: "Appear in AI Overviews, ChatGPT, Gemini and emerging AI platforms with content that gets discovered.",
    color: "#A78BFA",
    glow: "rgba(167,139,250,0.18)",
    cta: "Learn more",
  },
  {
    icon: Megaphone,
    title: "Google Ads Automation",
    desc: "Smart campaigns that adjust, optimize and scale automatically for maximum ROAS and growth.",
    color: "#FB923C",
    glow: "rgba(251,146,60,0.18)",
    cta: "Learn more",
  },
];

const METRICS = [
  { label: "Impressions", value: "12,272", delta: "+11.4%", color: "#00FF88", days: "Last 28 days" },
  { label: "Clicks", value: "1,267", delta: "+92.9%", color: "#A78BFA", days: "Last 28 days" },
  { label: "CTR", value: "1.18%", delta: "+82.9%", color: "#FB923C", days: "Last 28 days" },
  { label: "Avg. Position", value: "27.3", delta: "+32.9%", color: "#38BDF8", days: "Last 28 days" },
];

const FOOTER_BADGES = [
  { icon: Clock, text: "Works 24/7 in the background", color: "#00FF88" },
  { icon: Cloud, text: "Cloud-based & always up to date", color: "#A78BFA" },
  { icon: Lock, text: "Secure, private & your data stays yours", color: "#FB923C" },
  { icon: Rocket, text: "Built for results, designed for growth", color: "#38BDF8" },
];

const Sparkline = ({ color }: { color: string }) => (
  <svg viewBox="0 0 120 32" className="w-full h-8" preserveAspectRatio="none">
    <defs>
      <linearGradient id={`g-${color}`} x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.4" />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M0,24 L15,20 L25,22 L35,15 L45,18 L55,10 L65,14 L75,8 L85,12 L95,6 L105,10 L120,4 L120,32 L0,32 Z" fill={`url(#g-${color})`} />
    <path d="M0,24 L15,20 L25,22 L35,15 L45,18 L55,10 L65,14 L75,8 L85,12 L95,6 L105,10 L120,4" stroke={color} strokeWidth="1.5" fill="none" />
  </svg>
);

const DesktopHero = () => {
  const navigate = useNavigate();

  return (
    <div className="hidden md:block relative bg-[#03050A] text-white overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(0,255,136,0.4), transparent)" }}
      />

      <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* ── NAVBAR ───────────────────────── */}
        <header className="flex items-center justify-between gap-3 h-16 lg:h-20 overflow-hidden">
          <a href="/" className="flex items-center min-w-0 flex-shrink-0">
            <img src={logoFull} alt="OrbitRank" className="h-6 lg:h-8 w-auto max-w-[150px] lg:max-w-none object-contain" />
          </a>

          <nav className="flex items-center gap-2 lg:gap-7 xl:gap-9 min-w-0">
            {NAV_LINKS.map((l) => (
              l.caret ? (
                <DropdownMenu key={l.label}>
                  <DropdownMenuTrigger className="flex text-[11px] lg:text-[14px] font-medium text-white/85 hover:text-white transition-colors items-center gap-1 whitespace-nowrap outline-none">
                    {l.label}
                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#0B0E14] border-white/10 text-white">
                    <DropdownMenuItem className="cursor-pointer hover:bg-white/5"><BookOpen className="w-4 h-4 mr-2" /> Documentation</DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer hover:bg-white/5"><FileText className="w-4 h-4 mr-2" /> Case Studies</DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer hover:bg-white/5"><HelpCircle className="w-4 h-4 mr-2" /> Help Center</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => {
                    if (l.href.startsWith("#") && l.href.length > 1) {
                      const el = document.getElementById(l.href.slice(1));
                      if (el) {
                        e.preventDefault();
                        const y = el.getBoundingClientRect().top + window.scrollY - 120;
                        window.scrollTo({ top: y, behavior: "smooth" });
                      }
                    }
                  }}
                  className="flex text-[11px] lg:text-[14px] font-medium text-white/85 hover:text-white transition-colors items-center gap-1 whitespace-nowrap"
                >
                  {l.label}
                </a>
              )
            ))}
          </nav>

          <div className="flex items-center gap-2 lg:gap-5 flex-shrink-0">
            <button
              onClick={() => navigate("/auth")}
              className="text-[12px] lg:text-[14px] font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap"
            >
              Log in
            </button>
            <button
              onClick={() => navigate("/activate")}
              className="inline-flex items-center gap-2 h-10 px-3 lg:px-5 rounded-lg text-[12px] lg:text-[14px] font-semibold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-all whitespace-nowrap"
              style={{ boxShadow: "0 0 0 1px rgba(0,255,136,0.4), 0 8px 24px rgba(0,255,136,0.25)" }}
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* ── HERO ─────────────────────────── */}
        <section className="grid grid-cols-[minmax(0,0.92fr)_minmax(300px,0.88fr)] lg:grid-cols-[0.95fr_0.9fr] xl:grid-cols-2 gap-5 lg:gap-10 pt-8 lg:pt-10 pb-12 lg:pb-16 items-center">
          {/* LEFT */}
          <div>
            <div
              className="inline-flex items-center gap-2 h-7 px-3 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase mb-7"
              style={{
                color: "#00FF88",
                background: "rgba(0,255,136,0.08)",
                border: "1px solid rgba(0,255,136,0.35)",
                boxShadow: "0 0 24px rgba(0,255,136,0.15)",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88]" style={{ boxShadow: "0 0 8px #00FF88" }} />
              AI-Powered SEO Platform
            </div>

            <h1 className="text-[clamp(40px,5vw,50px)] lg:text-[clamp(54px,5vw,68px)] leading-[1.02] font-black tracking-normal mb-6 max-w-[680px]">
              <span className="text-white">Rank Everywhere.</span>
              <br />
              <span
                style={{
                  background: "linear-gradient(135deg, #00FF88 0%, #5EEAD4 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 0 14px rgba(0,255,136,0.18)",
                }}
              >
                Automatically.
              </span>
            </h1>

            <p className="text-[15px] lg:text-[17px] leading-[1.6] text-white/65 mb-8 lg:mb-9 max-w-[520px]">
              OrbitRank runs AI-powered SEO, AI Search &amp; Google Ads in the background — so your business climbs, grows and wins 24/7.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-8">
              <button
                onClick={() => navigate("/demo")}
                className="inline-flex items-center justify-center gap-2 h-12 px-5 lg:px-6 rounded-lg text-[14px] lg:text-[15px] font-bold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-all min-w-[186px]"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 1px rgba(0,255,136,0.45), 0 6px 18px rgba(0,255,136,0.18)" }}
              >
                Explore Live Demo <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center justify-center gap-2 h-12 px-5 lg:px-6 rounded-lg text-[14px] lg:text-[15px] font-semibold text-white border border-white/20 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/30 backdrop-blur-sm transition-all min-w-[154px]"
              >
                <Play className="w-4 h-4" /> How It Works
              </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-[560px]">
              {[
                { icon: Sparkles, t: "Real features", s: "Full system access", c: "#00FF88" },
                { icon: TrendingUp, t: "Live demo data", s: "See real results", c: "#A78BFA" },
                { icon: Shield, t: "No risk", s: "No credit card", c: "#FB923C" },
                { icon: RefreshCw, t: "Cancel anytime", s: "Stay in control", c: "#38BDF8" },
              ].map((item) => (
                <div key={item.t} className="flex items-start gap-2">
                  <item.icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: item.c }} />
                  <div>
                    <div className="text-[12px] font-bold text-white leading-tight">{item.t}</div>
                    <div className="text-[11px] text-white/55 leading-tight mt-0.5">{item.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT - Dashboard */}
          <div className="relative max-w-[430px] lg:max-w-none ml-auto">
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow:
                  "0 20px 50px -25px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.04)",
              }}
            >
              <img src={dashboardHero} alt="OrbitRank dashboard preview" className="w-full h-auto block" />
            </div>
          </div>
        </section>

        {/* ── TRUST BRIDGE (reviews card) ─── */}
        <div className="mb-6 -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-10">
          <TrustBridge />
        </div>

        {/* ── TRUSTED BY ───────────────────── */}
        <div
          className="rounded-2xl px-8 py-5 mb-6"
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="text-center text-[12px] text-[#00FF88] font-medium mb-3">
            Trusted by businesses that want to dominate search
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-8 lg:gap-x-12 gap-y-3">
            {TRUST_LOGOS.map((l) => (
              <span key={l} className="text-[15px] font-bold text-white/35 hover:text-white/60 transition-colors">
                ● {l}
              </span>
            ))}
          </div>
        </div>

        {/* ── 3 FEATURE CARDS ──────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          {FEATURE_CARDS.map((c) => (
            <div
              key={c.title}
              className="relative rounded-2xl p-6 overflow-hidden group transition-all hover:-translate-y-1"
              style={{
                background: `linear-gradient(160deg, ${c.glow}, rgba(255,255,255,0.02))`,
                border: `1px solid ${c.color}33`,
                boxShadow: `0 20px 40px -20px ${c.glow}`,
              }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${c.color}1a`, border: `1px solid ${c.color}40` }}
                >
                  <c.icon className="w-6 h-6" style={{ color: c.color }} />
                </div>
                <h3 className="text-[20px] font-bold text-white leading-tight pt-1.5">{c.title}</h3>
              </div>
              <p className="text-[14px] leading-[1.55] text-white/65 mb-5">{c.desc}</p>
              <a
                href="#"
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold transition-opacity hover:opacity-80"
                style={{ color: c.color }}
              >
                {c.cta} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          ))}
        </div>

        {/* ── METRICS BAR ──────────────────── */}
        <div
          className="rounded-2xl p-7 mb-6"
          style={{
            background: "linear-gradient(180deg, rgba(0,255,136,0.04), rgba(255,255,255,0.02))",
            border: "1px solid rgba(0,255,136,0.18)",
            boxShadow: "0 0 60px rgba(0,255,136,0.06) inset",
          }}
        >
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#00FF88] mb-5">
            Live Platform Results from our Demo Environment
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 items-center">
            {METRICS.map((m) => (
              <div key={m.label}>
                <div className="text-[36px] font-black text-white leading-none mb-1.5" style={{ letterSpacing: "-0.02em" }}>
                  {m.value}
                </div>
                <div className="text-[13px] text-white/65 mb-2">{m.label}</div>
                <div className="text-[12px] font-bold mb-2" style={{ color: m.color }}>
                  ↑ {m.delta}
                </div>
                <Sparkline color={m.color} />
                <div className="text-[10px] text-white/40 mt-1">{m.days}</div>
              </div>
            ))}
            <div
              className="rounded-xl p-5 -my-2"
              style={{
                background: "rgba(0,0,0,0.4)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div className="text-[14px] font-bold text-white mb-1.5">See OrbitRank in action</div>
              <div className="text-[11px] text-white/60 leading-snug mb-3">
                Explore the live demo workspace with real data, real results and full platform access.
              </div>
              <button
                onClick={() => navigate("/activate")}
                className="w-full h-10 rounded-lg text-[13px] font-bold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-colors"
                style={{ boxShadow: "0 8px 20px rgba(0,255,136,0.3)" }}
              >
                Start Free Trial →
              </button>
              <div className="text-[10px] text-white/45 text-center mt-2">— No credit card required —</div>
            </div>
          </div>
        </div>

        {/* ── FOOTER BADGES ────────────────── */}
        <div
          className="rounded-2xl px-6 lg:px-8 py-5 mb-12 flex flex-wrap items-center justify-center lg:justify-between gap-4"
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {FOOTER_BADGES.map((b) => (
            <div key={b.text} className="flex items-center gap-2.5">
              <b.icon className="w-4 h-4" style={{ color: b.color }} />
              <span className="text-[13px] text-white/70 font-medium">{b.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DesktopHero;
